import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import { DEFAULT_GC_TIME, DEFAULT_STALE_TIME } from '@/client/constants';
import {
  Voucher,
  VoucherListParams,
  VoucherRedemptionsResponse,
  VoucherScopesResponse,
  VoucherType,
  VoucherUsage,
  VoucherTypesResponse,
  VouchersListResponse,
} from '@/client/types/voucher';

const BASE = '/api/admin/v2/vouchers';

export const voucherKeys = {
  all: ['vouchers-v2'] as const,
  lists: () => [...voucherKeys.all, 'list'] as const,
  list: (params: VoucherListParams) => [...voucherKeys.lists(), params] as const,
  detail: (vid: number) => [...voucherKeys.all, 'detail', vid] as const,
  usage: (vid: number) => [...voucherKeys.all, 'usage', vid] as const,
  scopes: (vid: number) => [...voucherKeys.all, 'scopes', vid] as const,
  redemptions: (vid: number, page: number) =>
    [...voucherKeys.all, 'redemptions', vid, page] as const,
  types: () => [...voucherKeys.all, 'types'] as const,
  statusCount: (status: string) => [...voucherKeys.all, 'status-count', status] as const,
};

const buildSearchParams = (raw: Record<string, unknown>): string => {
  const params = Object.entries(raw).reduce<Record<string, string>>((acc, [k, v]) => {
    if (v !== undefined && v !== null && v !== '') acc[k] = String(v);
    return acc;
  }, {});
  const qs = new URLSearchParams(params).toString();
  return qs ? `?${qs}` : '';
};

// ─── Types ────────────────────────────────────────────────────────────────────

const useVoucherTypesFn = () => {
  const request = useClientStore((s) => s.request);
  return withErrorHandling(async (): Promise<VoucherType[]> => {
    const response = await request<VoucherTypesResponse>(`${BASE}/types`);
    return response.voucher_types ?? [];
  }, 'Failed to fetch voucher types');
};

export const useVoucherTypesQuery = () => {
  const queryFn = useVoucherTypesFn();
  return useQuery({
    queryKey: voucherKeys.types(),
    queryFn,
    staleTime: DEFAULT_STALE_TIME * 12,
    gcTime: DEFAULT_GC_TIME,
  });
};

// ─── List ─────────────────────────────────────────────────────────────────────

const useVoucherListFn = () => {
  const request = useClientStore((s) => s.request);
  return withErrorHandling(async (params: VoucherListParams = {}): Promise<VouchersListResponse> => {
    const qs = buildSearchParams(params as Record<string, unknown>);
    return request<VouchersListResponse>(`${BASE}${qs}`);
  }, 'Failed to fetch vouchers');
};

export const useVoucherListQuery = (params: VoucherListParams = {}, enabled = true) => {
  const queryFn = useVoucherListFn();
  return useQuery({
    queryKey: voucherKeys.list(params),
    queryFn: () => queryFn(params),
    enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

/** Lightweight count for a status (limit=1 → pagination.total). */
export const useVoucherStatusCountQuery = (status: string, enabled = true) => {
  const queryFn = useVoucherListFn();
  return useQuery({
    queryKey: voucherKeys.statusCount(status),
    queryFn: async () => {
      const res = await queryFn({ status: status as VoucherListParams['status'], limit: 1, offset: 0 });
      return res.pagination?.total ?? 0;
    },
    enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

// ─── Detail ───────────────────────────────────────────────────────────────────

const useVoucherDetailFn = () => {
  const request = useClientStore((s) => s.request);
  return withErrorHandling(async (vid: number): Promise<Voucher> => {
    return request<Voucher>(`${BASE}/${vid}`);
  }, 'Failed to fetch voucher details');
};

export const useVoucherDetailQuery = (vid: number | null) => {
  const queryFn = useVoucherDetailFn();
  return useQuery({
    queryKey: voucherKeys.detail(vid ?? 0),
    queryFn: () => queryFn(vid!),
    enabled: vid != null && vid > 0,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

// ─── Usage ────────────────────────────────────────────────────────────────────

const useVoucherUsageFn = () => {
  const request = useClientStore((s) => s.request);
  return withErrorHandling(async (vid: number): Promise<VoucherUsage> => {
    return request<VoucherUsage>(`${BASE}/${vid}/usage`);
  }, 'Failed to fetch voucher usage');
};

export const useVoucherUsageQuery = (vid: number | null, enabled = true) => {
  const queryFn = useVoucherUsageFn();
  return useQuery({
    queryKey: voucherKeys.usage(vid ?? 0),
    queryFn: () => queryFn(vid!),
    enabled: enabled && vid != null && vid > 0,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

// ─── Redemptions ──────────────────────────────────────────────────────────────

const useVoucherRedemptionsFn = () => {
  const request = useClientStore((s) => s.request);
  return withErrorHandling(
    async (vid: number, limit = 50, offset = 0): Promise<VoucherRedemptionsResponse> => {
      const qs = buildSearchParams({ limit, offset });
      return request<VoucherRedemptionsResponse>(`${BASE}/${vid}/redemptions${qs}`);
    },
    'Failed to fetch voucher redemptions',
  );
};

export const useVoucherRedemptionsQuery = (
  vid: number | null,
  page = 1,
  pageSize = 25,
  enabled = true,
) => {
  const queryFn = useVoucherRedemptionsFn();
  const offset = (page - 1) * pageSize;
  return useQuery({
    queryKey: voucherKeys.redemptions(vid ?? 0, page),
    queryFn: () => queryFn(vid!, pageSize, offset),
    enabled: enabled && vid != null && vid > 0,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

// ─── Scopes ───────────────────────────────────────────────────────────────────

const useVoucherScopesFn = () => {
  const request = useClientStore((s) => s.request);
  return withErrorHandling(async (vid: number): Promise<VoucherScopesResponse> => {
    return request<VoucherScopesResponse>(`${BASE}/${vid}/scopes`);
  }, 'Failed to fetch voucher scopes');
};

export const useVoucherScopesQuery = (vid: number | null, enabled = true) => {
  const queryFn = useVoucherScopesFn();
  return useQuery({
    queryKey: voucherKeys.scopes(vid ?? 0),
    queryFn: () => queryFn(vid!),
    enabled: enabled && vid != null && vid > 0,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};
